import React, { useState } from 'react';
import { apiCall } from '../services/api';
import '../styles/EngineGlassmorphism.css';

export default function CommentMining({ user, onCreditsUpdate }) {
  const [videoId, setVideoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [userCredits, setUserCredits] = useState(user?.credits || 0);

  const handleMine = async (e) => {
    e?.preventDefault();
    if (!videoId.trim()) {
      setError('Please enter a YouTube Video URL or Video ID');
      return;
    }

    setLoading(true);
    setError('');
    setData(null);

    try {
      const response = await apiCall('/analysis/mine-comments', 'POST', { videoId: videoId.trim() });
      if (response.success && response.data) {
        setData(response.data);
        if (response.credits && onCreditsUpdate) {
          setUserCredits(response.credits.remaining);
          onCreditsUpdate(response.credits.remaining);
        }
      } else {
        setError('Failed to mine comments for target video.');
      }
    } catch (err) {
      if (err.message === 'INSUFFICIENT_CREDITS' && err.details) {
        setError(`Insufficient credits. Required: 10, Available: ${err.details.available || 0}`);
      } else {
        setError(err.message || 'Comment mining failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="engine-dashboard" style={{ padding: '1rem 0' }}>
      <header className="engine-header" style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, background: 'linear-gradient(90deg, #ec4899, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>
          💬 Audience Comment Mining & Sentiment Intelligence
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '0.3rem' }}>
          Extract real viewer questions, sentiment distribution, and high-demand content opportunities from YouTube comments.
        </p>
      </header>

      {/* Input Panel */}
      <div className="engine-glass-panel" style={{ marginBottom: '2rem' }}>
        <form onSubmit={handleMine}>
          <div className="engine-input-group">
            <input
              type="text"
              className="engine-input"
              placeholder="Paste YouTube Video URL or Video ID (e.g., 'https://www.youtube.com/watch?v=...')"
              value={videoId}
              onChange={(e) => setVideoId(e.target.value)}
              disabled={loading}
            />
            <button type="submit" disabled={loading || !videoId.trim()} className="engine-btn" style={{ background: 'linear-gradient(90deg, #ec4899, #8b5cf6)' }}>
              {loading ? '⏳ Mining...' : '💬 Mine Comments'}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.8rem', fontSize: '0.85rem', color: '#94a3b8' }}>
            <span>💳 Cost: <strong>10 Credits</strong> per mining run</span>
            <span>Balance: <strong style={{ color: '#ec4899' }}>{userCredits}</strong> Credits</span>
          </div>
        </form>

        {error && <div className="engine-error" style={{ marginTop: '1rem' }}>❌ {error}</div>}
      </div>

      {/* Results View */}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Sentiment Summary Card */}
          <div className="engine-glass-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.2rem' }}>Audience Sentiment Overview</h3>
                <p style={{ margin: '0.3rem 0 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                  Target Video: <strong style={{ color: '#cbd5e1' }}>{data.videoId}</strong> • Total Mined: <strong style={{ color: '#8b5cf6' }}>{data.totalCommentsMined} comments</strong> {data.isLiveComments && '(Live YouTube API)'}
                </p>
              </div>
              <span style={{
                background: data.sentimentAnalysis?.overallSentiment === 'Positive' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                color: data.sentimentAnalysis?.overallSentiment === 'Positive' ? '#34d399' : '#fcd34d',
                padding: '0.4rem 1rem', borderRadius: '9999px', fontWeight: 700, fontSize: '0.9rem'
              }}>
                Overall: {data.sentimentAnalysis?.overallSentiment}
              </span>
            </div>

            {/* Sentiment Bar */}
            <div style={{ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', margin: '1rem 0' }}>
              <div style={{ width: `${data.sentimentAnalysis?.positivePct || 0}%`, background: '#10b981' }} title={`Positive: ${data.sentimentAnalysis?.positivePct}%`}></div>
              <div style={{ width: `${data.sentimentAnalysis?.neutralPct || 0}%`, background: '#64748b' }} title={`Neutral: ${data.sentimentAnalysis?.neutralPct}%`}></div>
              <div style={{ width: `${data.sentimentAnalysis?.negativePct || 0}%`, background: '#ef4444' }} title={`Negative: ${data.sentimentAnalysis?.negativePct}%`}></div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', fontWeight: 600 }}>
              <span style={{ color: '#34d399' }}>🟢 Positive: {data.sentimentAnalysis?.positivePct}%</span>
              <span style={{ color: '#94a3b8' }}>⚪ Neutral: {data.sentimentAnalysis?.neutralPct}%</span>
              <span style={{ color: '#f87171' }}>🔴 Negative: {data.sentimentAnalysis?.negativePct}%</span>
            </div>
          </div>

          {/* Extracted Questions Card */}
          {data.questionsExtracted && data.questionsExtracted.length > 0 && (
            <div className="engine-glass-panel">
              <h3 style={{ color: '#c084fc', marginBottom: '1rem', fontSize: '1.2rem' }}>
                ❓ Extracted Viewer Questions ({data.questionsExtracted.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {data.questionsExtracted.map((q, idx) => (
                  <div key={idx} style={{ background: 'rgba(0,0,0,0.3)', padding: '1rem', borderRadius: '10px', borderLeft: '4px solid #8b5cf6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: '#f8fafc', fontSize: '1rem', display: 'block' }}>"{q.question}"</strong>
                      {q.author && <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.3rem', display: 'block' }}>Asked by {q.author}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ background: 'rgba(139,92,246,0.2)', color: '#c084fc', padding: '0.3rem 0.7rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700 }}>
                        👍 {q.likes} likes
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Opportunities */}
          {data.contentOpportunities && (
            <div className="engine-glass-panel">
              <h3 style={{ color: '#f472b6', marginBottom: '1rem', fontSize: '1.2rem' }}>
                💡 High-Demand Video Topic Opportunities
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                {data.contentOpportunities.highDemandTopics?.map((item, idx) => (
                  <div key={idx} style={{ background: 'rgba(0,0,0,0.3)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(236,72,153,0.2)' }}>
                    <span style={{ background: 'rgba(236,72,153,0.2)', color: '#f472b6', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 }}>
                      Demand: {item.demandLevel}
                    </span>
                    <strong style={{ color: '#f8fafc', fontSize: '0.95rem', display: 'block', margin: '0.5rem 0' }}>{item.suggestedVideoTitle}</strong>
                    <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: 0 }}>Based on question: "{item.topic}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
